import React, { forwardRef } from "react";

// 말 언어 문해기초 선별 체크리스트 결과보고서 - 1페이지 (분석 결과)
// dom-to-image로 캡처되는 A4(794x1123px) 정적 레이아웃.
// 한글 깨짐 방지를 위해 fontFamily "Noto Sans KR" 명시.

const A4_STYLE = {
  width: "794px",
  minHeight: "1123px",
  backgroundColor: "#ffffff",
  boxSizing: "border-box",
  fontFamily: "'Noto Sans KR', sans-serif",
  padding: "46px 50px",
  color: "#1a1a1a",
};

const evalColor = (ev) =>
  ev === "양호" ? "#2e9e4f" : ev === "주의" ? "#e8920c" : "#e0352b";
const evalBg = (ev) =>
  ev === "양호" ? "#eaf7ee" : ev === "주의" ? "#fdf3e2" : "#fcebe9";

const fmtDate = (d) => {
  if (!d) return "";
  if (typeof d === "string") return d.split("T")[0];
  if (typeof d.format === "function") return d.format("YYYY-MM-DD");
  if (d.$d instanceof Date) {
    const dt = d.$d;
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const day = String(dt.getDate()).padStart(2, "0");
    return `${dt.getFullYear()}-${m}-${day}`;
  }
  return "";
};

const labelCell = {
  backgroundColor: "#eef0f6",
  fontWeight: 600,
  padding: "9px 12px",
  fontSize: "15px",
  textAlign: "center",
  border: "1px solid #d9dce3",
  width: "18%",
  whiteSpace: "nowrap",
};
const valueCell = {
  backgroundColor: "#ffffff",
  padding: "9px 12px",
  fontSize: "15px",
  textAlign: "center",
  border: "1px solid #d9dce3",
  width: "32%",
};
const summaryHead = {
  backgroundColor: "#eef0f6",
  border: "1px solid #d9dce3",
  padding: "10px",
  fontSize: "16px",
  fontWeight: 600,
  textAlign: "center",
};
const summaryScore = {
  border: "1px solid #d9dce3",
  padding: "12px",
  fontSize: "18px",
  textAlign: "center",
  fontWeight: 600,
};
const sectionTitle = {
  marginTop: "32px",
  fontSize: "19px",
  fontWeight: 700,
  color: "#003366",
};

const SLEReportPage1 = forwardRef((props, ref) => {
  const {
    info = {},
    categoryAScore,
    categoryBScore,
    totalScore,
    totalEvaluation,
    categoryAEvaluation,
    categoryBEvaluation,
    recommendationHtml,
  } = props;

  const evalCellStyle = (ev) => ({
    border: "1px solid #d9dce3",
    padding: "12px",
    fontSize: "18px",
    textAlign: "center",
    fontWeight: 700,
    color: evalColor(ev),
    backgroundColor: evalBg(ev),
  });

  return (
    <div ref={ref} style={A4_STYLE}>
      {/* 헤더 */}
      <div
        style={{
          backgroundColor: "#003366",
          color: "#ffffff",
          textAlign: "center",
          padding: "18px",
          borderRadius: "6px",
          fontSize: "23px",
          fontWeight: 700,
          letterSpacing: "0.5px",
        }}
      >
        말 언어 문해기초 선별 체크리스트 결과보고서
      </div>

      {/* 발화자 / 평가자 정보 */}
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          marginTop: "30px",
          tableLayout: "fixed",
        }}
      >
        <tbody>
          <tr>
            <td style={labelCell}>이름</td>
            <td style={valueCell}>{info.name}</td>
            <td style={labelCell}>성별</td>
            <td style={valueCell}>{info.gender}</td>
          </tr>
          <tr>
            <td style={labelCell}>생년월일</td>
            <td style={valueCell}>{fmtDate(info.birthday)}</td>
            <td style={labelCell}>검사일</td>
            <td style={valueCell}>{fmtDate(info.examdate)}</td>
          </tr>
          <tr>
            <td style={labelCell}>교육기관</td>
            <td style={valueCell}>{info.school}</td>
            <td style={labelCell}>학년</td>
            <td style={valueCell}>{info.grade}</td>
          </tr>
          <tr>
            <td style={labelCell}>검사자</td>
            <td style={valueCell}>{info.examiner}</td>
            <td style={labelCell}>평가지역</td>
            <td style={valueCell}>{info.region}</td>
          </tr>
          <tr>
            <td style={labelCell}>평가기관</td>
            <td style={valueCell} colSpan={3}>
              {info.institution}
            </td>
          </tr>
        </tbody>
      </table>

      {/* 결과 요약 */}
      <div style={sectionTitle}>결과 요약</div>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          marginTop: "12px",
          tableLayout: "fixed",
        }}
      >
        <thead>
          <tr>
            <th style={summaryHead}>총점</th>
            <th style={summaryHead}>언어 이해 및 표현</th>
            <th style={summaryHead}>말 및 문해</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={summaryScore}>{totalScore}점</td>
            <td style={summaryScore}>{categoryAScore}점</td>
            <td style={summaryScore}>{categoryBScore}점</td>
          </tr>
          <tr>
            <td style={evalCellStyle(totalEvaluation)}>{totalEvaluation}</td>
            <td style={evalCellStyle(categoryAEvaluation)}>
              {categoryAEvaluation}
            </td>
            <td style={evalCellStyle(categoryBEvaluation)}>
              {categoryBEvaluation}
            </td>
          </tr>
        </tbody>
      </table>

      {/* 권고 */}
      <div style={sectionTitle}>결과 해석 및 권고</div>
      <div
        style={{
          marginTop: "12px",
          border: "1px solid #d9dce3",
          borderRadius: "6px",
          padding: "20px 22px",
          fontSize: "15px",
          lineHeight: 1.9,
          textAlign: "left",
        }}
        dangerouslySetInnerHTML={{ __html: recommendationHtml }}
      />

      {/* 푸터 */}
      <div
        style={{
          marginTop: "40px",
          fontSize: "12px",
          color: "#888888",
          textAlign: "center",
          lineHeight: 1.7,
        }}
      >
        말 언어 문해기초 선별 체크리스트 (Speech Language Early Literacy
        Screening Checklist)
        <br />
        한림대학교 언어청각학부 (배소영 · 하승희 · 엄지목 · 유해림) | Speech4All
      </div>
    </div>
  );
});

export default SLEReportPage1;
